import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Star, FileText } from "lucide-react";
import { GetResourceSearchResultsItem } from "@/api/getResourceSearchResults/types";
import { useAppDispatch } from "@/app/hooks";
import { setScrollPosition } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { FavoriteButton } from "../FavoriteButton/FavoriteButton";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";

interface ResourceListItemProps {
  resource: GetResourceSearchResultsItem;
  onToggleFavorite?: (uuid: string, isFavorited: boolean) => void;
}

export function ResourceListItem({
  resource,
  onToggleFavorite,
}: ResourceListItemProps) {
  const dispatch = useAppDispatch();
  const [isFavorited, setIsFavorited] = useState(resource.isFavorited ?? false);

  const handleCardClick = () => {
    const scrollPosition =
      window.scrollY ||
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    dispatch(setScrollPosition(scrollPosition));
  };

  const handleToggleFavorite = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Optimistically update UI
    const previousState = isFavorited;
    setIsFavorited(!isFavorited);

    try {
      await toggleResourceFavorite({ resourceUuid: resource.uuid });
      // If parent callback exists, notify parent of change
      if (onToggleFavorite) {
        onToggleFavorite(resource.uuid, !previousState);
      }
    } catch (error) {
      // Revert on error
      setIsFavorited(previousState);
      throw error; // Let FavoriteButton handle the error toast
    }
  };

  // Check if this is a Partner Page that should open externally
  const isPartnerPage =
    resource.resourceType === "Partner Page" && resource.contentUrl;

  const contentType = (resource.resourceType || "document").toLowerCase();

  const content = (
    <div className="flex gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all group">
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-32 h-32 relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700">
        {resource.thumbnailUrl ? (
          <img
            src={resource.thumbnailUrl}
            alt={resource.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span
              className="inline-block opacity-50"
              dangerouslySetInnerHTML={{
                __html: resource.primaryIcon ?? "",
              }}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          {/* Title and Type Badge */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
                {resource.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="text-xs font-medium px-2 py-1 rounded-full inline-block"
                  style={{
                    backgroundColor: resource.backgroundColor ?? "#ccc",
                    color: resource.textColor ?? "#000",
                    border: resource.borderColor
                      ? `1px solid ${resource.borderColor}`
                      : "none",
                  }}
                >
                  {resource.resourceType || "Document"}
                </span>
                {resource.isFeatured && (
                  <span className="bg-yellow-400 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-100 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    FEATURED
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with views and favorite button */}
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Eye className="h-4 w-4" />
            {resource.viewCount || 0} views
          </span>
          <FavoriteButton
            isFavorited={isFavorited}
            onToggle={handleToggleFavorite}
          />
        </div>
      </div>
    </div>
  );

  if (isPartnerPage) {
    return (
      <a
        href={resource.contentUrl || ""}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCardClick}
        className="block"
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={`/hub/resources/${resource.slug}`}
      onClick={handleCardClick}
      className="block"
    >
      {content}
    </Link>
  );
}